import React, { useRef, useEffect, useState } from "react";

/**
 * OrganogramDiagram.js
 * React component that draws a curved SVG tree organogram for BEQAEB.
 *
 * Usage:
 *  <OrganogramDiagram />
 *
 * No external libs required.
 */

// --- Organisation data (structured) ---
const treeData = {
  name: "Governor / Ministry of Education",
  children: [
    {
      name: "Office of the Executive Secretary",
      children: [
        {
          name: "Internal Units",
          children: [
            { name: "Strategic Planning & Policy Desk" },
            { name: "Legal & Regulatory Affairs Unit" },
            { name: "Internal Audit Unit" },
            { name: "Students Exchange Programme Office" },
            { name: "Board Secretariat & Special Duties" },
            { name: "Public Relations & Corporate Communications" },
            { name: "Maintenance & Transportation Unit" },
            { name: "Special Duties & External Relations" },
            { name: "SERVICOM / Public Complaints & Feedback Desk" },
          ],
        },
      ],
    },
    {
      name: "Directorate of Quality Assurance",
      children: [
        {
          name: "Schools Monitoring & Evaluation Dept",
          children: [
            { name: "Private Schools Compliance Unit" },
            { name: "Public Schools Compliance Unit" },
            { name: "Special Needs & Inclusive Education Unit" },
            { name: "Early Childhood & Basic Education Unit" },
            { name: "Post-Basic Education Monitoring Unit" },
          ],
        },
        {
          name: "Standards & Compliance Dept",
          children: [
            { name: "School Accreditation & Licensing Unit" },
            { name: "Teacher Professional Conduct & Certification Liaison Unit" },
            { name: "Safety, Child Protection & School Ethics Unit" },
            { name: "Data Verification & Field Reporting Unit (LGA Offices)" },
          ],
        },
        {
          name: "Research & Evaluation Dept",
          children: [
            { name: "School Performance Assessments Unit" },
            { name: "Annual Education Quality Survey Unit" },
            { name: "Policy & Standards Review Unit" },
          ],
        },
      ],
    },
    {
      name: "Directorate of Examinations & Assessment",
      children: [
        {
          name: "Examination Administration Dept",
          children: [
            { name: "BECE Unit" },
            { name: "MOCK SSCE / Placement Exams Unit" },
            { name: "Professional & Technical Exams Unit" },
            { name: "Special Exams & Remedial Programs Unit" },
          ],
        },
        {
          name: "Exams Planning & Logistics Dept",
          children: [
            { name: "Question Setting & Moderation Unit" },
            { name: "Printing & Security Unit" },
            { name: "Distribution & Exam Centre Management Unit" },
          ],
        },
        {
          name: "Results, Records & Certification Dept",
          children: [
            { name: "Marking & Results Processing Unit" },
            { name: "Certification & Verification Unit" },
            { name: "Exams Malpractice Investigation Unit" },
          ],
        },
      ],
    },
    {
      name: "Directorate of ICT & Digital Innovation",
      children: [
        { name: "Digital Exams & CBT Development Unit" },
        { name: "Data Management & EMIS Unit" },
        { name: "Website & Social Media Management Unit" },
        { name: "ICT Support & Maintenance Unit" },
        { name: "e-Learning & Digital Transformation Unit" },
      ],
    },
    {
      name: "Directorate of Finance & Administration",
      children: [
        {
          name: "Administration & Human Resources",
          children: [
            { name: "Recruitment, Promotion & Discipline Unit" },
            { name: "Training & Capacity Building Unit" },
            { name: "Welfare & Pension Desk" },
          ],
        },
        {
          name: "Finance & Accounts",
          children: [
            { name: "Budget & Planning Unit" },
            { name: "Treasury / Accounts Unit" },
            { name: "Procurement Unit" },
            { name: "Store & Inventory Unit" },
          ],
        },
      ],
    },
    {
      name: "Directorate of Planning, Partnerships & Int'l Cooperation",
      children: [
        { name: "Donor & Development Partner Relations Unit" },
        { name: "Education Investment & PPP Desk" },
        { name: "Monitoring Donor-Funded Projects Unit" },
        { name: "Research & Innovation Collaboration Desk" },
      ],
    },
    {
      name: "Directorate of Enforcement & Compliance Operations",
      children: [
        { name: "Compliance Enforcement Unit" },
        { name: "Regulatory Compliance Unit" },
        { name: "Investigation & Intelligence Unit" },
      ],
    },
  ],
};

// --- Layout helpers: compute leaf counts and X positions ---
function computeLeafCount(node) {
  if (!node.children || node.children.length === 0) return 1;
  return node.children.reduce((sum, ch) => sum + computeLeafCount(ch), 0);
}

function assignPositions(node, depth = 0, xStart = 0, hSpacing = 220, vSpacing = 120) {
  // returns { nodeWithPos, widthInLeaves }
  const nodeCopy = { ...node };
  nodeCopy.depth = depth;

  if (!node.children || node.children.length === 0) {
    nodeCopy.x = xStart + hSpacing / 2;
    nodeCopy.y = depth * vSpacing;
    nodeCopy.width = 1;
    return { node: nodeCopy, width: 1 };
  }

  let currX = xStart;
  const childrenWithPos = [];
  let totalLeaves = 0;

  for (let ch of node.children) {
    const leafCount = computeLeafCount(ch);
    const assigned = assignPositions(ch, depth + 1, currX, hSpacing, vSpacing);
    childrenWithPos.push(assigned.node);
    currX += leafCount * hSpacing;
    totalLeaves += leafCount;
  }

  // node.x is middle of children span
  const firstChild = childrenWithPos[0];
  const lastChild = childrenWithPos[childrenWithPos.length - 1];
  nodeCopy.children = childrenWithPos;
  nodeCopy.width = totalLeaves;
  nodeCopy.x = (firstChild.x + lastChild.x) / 2;
  nodeCopy.y = depth * vSpacing;

  return { node: nodeCopy, width: totalLeaves };
}

// helper to create smooth curved path between parent and child
function linkPath(parent, child) {
  const startX = parent.x;
  const startY = parent.y + 28; // node height offset
  const endX = child.x;
  const endY = child.y - 18;
  const dx = Math.abs(endX - startX);
  const controlOffset = Math.max(40, dx * 0.4);

  const c1x = startX;
  const c1y = startY + controlOffset;
  const c2x = endX;
  const c2y = endY - controlOffset;

  return `M ${startX} ${startY} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${endX} ${endY}`;
}

// flatten nodes helper
function flatten(node, out = []) {
  out.push(node);
  if (node.children) {
    for (let ch of node.children) flatten(ch, out);
  }
  return out;
}

// --- React component ---
const OrganogramDiagram = () => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tree, setTree] = useState(() => assignPositions(treeData).node);
  const [viewBox, setViewBox] = useState({ x: 0, y: 0, w: 1200, h: 800 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, vbX: 0, vbY: 0 });

  useEffect(() => {
    // recompute positions based on widths
    const hSpacing = 220;
    const vSpacing = 120;
    const rootAssigned = assignPositions(treeData, 0, 0, hSpacing, vSpacing).node;
    const all = flatten(rootAssigned);
    const maxX = Math.max(...all.map((n) => n.x)) + 200;
    const maxY = Math.max(...all.map((n) => n.y)) + 200;
    setTree(rootAssigned);
    setViewBox({ x: 0, y: 0, w: Math.max(1200, maxX + 100), h: Math.max(800, maxY + 100) });
    // center initial view
    // setViewBox((vb) => ({ ...vb, x: 0 }));
  }, []);

  // --- Pan and zoom handlers ---
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const onWheel = (e) => {
      e.preventDefault();
      const zoomSensitivity = 0.0015;
      const delta = e.deltaY;
      const scaleFactor = 1 + delta * zoomSensitivity;
      // adjust viewBox width/height about mouse point
      const rect = svg.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      setViewBox((vb) => {
        const worldX = vb.x + (mx / rect.width) * vb.w;
        const worldY = vb.y + (my / rect.height) * vb.h;
        const newW = Math.max(400, vb.w * scaleFactor);
        const newH = Math.max(300, vb.h * scaleFactor);
        const nx = worldX - (mx / rect.width) * newW;
        const ny = worldY - (my / rect.height) * newH;
        return { x: nx, y: ny, w: newW, h: newH };
      });
    };

    const onMouseDown = (e) => {
      setIsPanning(true);
      panStart.current = { x: e.clientX, y: e.clientY, vbX: viewBox.x, vbY: viewBox.y };
    };

    const onMouseMove = (e) => {
      if (!isPanning) return;
      const dx = (e.clientX - panStart.current.x) * (viewBox.w / svg.clientWidth);
      const dy = (e.clientY - panStart.current.y) * (viewBox.h / svg.clientHeight);
      setViewBox((vb) => ({ ...vb, x: panStart.current.vbX - dx, y: panStart.current.vbY - dy }));
    };

    const onMouseUp = () => setIsPanning(false);

    svg.addEventListener("wheel", onWheel, { passive: false });
    svg.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      svg.removeEventListener("wheel", onWheel);
      svg.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgRef, viewBox, isPanning]);

  const allNodes = flatten(tree);

  // download handler: convert SVG to PNG and trigger download
  const downloadPNG = async () => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgEl);
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const image = new Image();
    const rect = svgEl.getBoundingClientRect();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1200, svgEl.viewBox.baseVal.width);
      canvas.height = Math.max(800, svgEl.viewBox.baseVal.height);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0);
      canvas.toBlob((pngBlob) => {
        const link = document.createElement("a");
        link.download = "beqaeb-organogram.png";
        link.href = URL.createObjectURL(pngBlob);
        link.click();
        URL.revokeObjectURL(link.href);
      }, "image/png");
      URL.revokeObjectURL(url);
    };
    image.crossOrigin = "anonymous";
    image.src = url;
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-blue-900">BEQAEB Organogram</h2>
        <div className="space-x-2">
          <button
            onClick={() =>
              setViewBox((vb) => ({ x: 0, y: 0, w: Math.max(1200, vb.w), h: Math.max(800, vb.h) }))
            }
            className="px-3 py-1 bg-white border rounded shadow-sm text-sm"
          >
            Reset View
          </button>
          <button onClick={downloadPNG} className="px-3 py-1 bg-blue-600 text-white rounded shadow-sm text-sm">
            Download PNG
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        style={{ borderRadius: 12, background: "#fff" }}
        className="shadow-lg overflow-hidden"
      >
        <svg
          ref={svgRef}
          viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
          width="100%"
          height="720"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* background */}
          <defs>
            <linearGradient id="nodeGrad" x1="0" x2="1">
              <stop offset="0%" stopColor="#0ea5e9" />
              <stop offset="100%" stopColor="#0369a1" />
            </linearGradient>
            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000" floodOpacity="0.15" />
            </filter>
          </defs>

          {/* connectors */}
          <g fill="none" stroke="#60a5fa" strokeWidth="2">
            {allNodes.map((n, i) => {
              if (!n.children) return null;
              return n.children.map((ch, ci) => (
                <path key={`${i}-${ci}`} d={linkPath(n, ch)} strokeOpacity="0.85" stroke="#60a5fa" fill="none" />
              ));
            })}
          </g>

          {/* nodes */}
          <g fontFamily="Inter, system-ui, sans-serif">
            {allNodes.map((n, idx) => {
              const nodeWidth = 220;
              const nodeHeight = 56;
              const x = n.x - nodeWidth / 2;
              const y = n.y - nodeHeight / 2;
              const rx = 12;
              // short label helper: split into two lines if long
              const label = n.name;
              const words = label.split(" ");
              let line1 = label;
              let line2 = null;
              if (label.length > 28) {
                // crude split
                const mid = Math.floor(words.length / 2);
                line1 = words.slice(0, mid).join(" ");
                line2 = words.slice(mid).join(" ");
              }

              return (
                <g key={idx} transform={`translate(${x}, ${y})`} filter="url(#shadow)">
                  <rect
                    x={0}
                    y={0}
                    width={nodeWidth}
                    height={nodeHeight}
                    rx={rx}
                    fill="url(#nodeGrad)"
                    stroke="#065f8d"
                    strokeWidth="1"
                  />
                  <text
                    x={nodeWidth / 2}
                    y={nodeHeight / 2 - (line2 ? 6 : 0)}
                    textAnchor="middle"
                    alignmentBaseline="middle"
                    fontSize="12.5"
                    fill="#ffffff"
                    style={{ fontWeight: 700 }}
                  >
                    {line1}
                  </text>
                  {line2 && (
                    <text
                      x={nodeWidth / 2}
                      y={nodeHeight / 2 + 12}
                      textAnchor="middle"
                      alignmentBaseline="middle"
                      fontSize="12"
                      fill="#e6f7ff"
                    >
                      {line2}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
      </div>

      <p className="text-sm text-gray-600 mt-3">
        Tip: Use mouse wheel to zoom, drag to pan. Click nodes to expand/collapse (not implemented for this SVG layout).
      </p>
    </div>
  );
};

export default OrganogramDiagram;
